/**
 * Spin Wheel — pure, side-effect-free helpers for the operator-configured prize
 * game. Kept here (per packages/shared rules) so the whole prize-selection
 * invariant and the id/odds maths are unit-tested in isolation rather than
 * exercised through Firestore + Cloud Functions.
 *
 * THE ANTI-CHEAT CRUX lives in `pickWinningSegmentIndex`: the winning slice is
 * chosen SERVER-SIDE from a securely-drawn integer. The Cloud Function computes
 * `crypto.randomInt(totalWeight)` (a CSPRNG draw the client can neither see nor
 * influence) and hands it to this pure function, which maps it to a segment via
 * cumulative weights. The client only ANIMATES the wheel to the returned index —
 * it can never predict or bias the outcome. Splitting the draw (impure, in the
 * function) from the mapping (pure, here) lets us test the weighted distribution
 * deterministically by INJECTING the drawn integer.
 */

/** Minimum segments a wheel needs before it is playable (a real choice). */
export const SPIN_WHEEL_MIN_SEGMENTS = 2;

/**
 * Map a securely-drawn integer to the winning segment index via cumulative
 * weights.
 *
 * `draw` MUST be an integer in `[0, totalWeight)` where
 * `totalWeight = sum(weights)` — exactly what `crypto.randomInt(totalWeight)`
 * yields. Each weight is expected to be a positive integer (the schema enforces
 * `>= 1`), so every segment has a non-zero chance and the walk always resolves.
 *
 * Determinism: given the same `weights` + `draw` it always returns the same
 * index, so the whole distribution is unit-tested by sweeping `draw` across the
 * boundary of every cumulative bucket — no randomness in the test.
 *
 * Defensive edges (never expected in production — the callable checks
 * playability first): an empty `weights` returns `-1`; a `draw` at/above the
 * total clamps to the last index; a negative `draw` clamps to the first. These
 * keep a corrupt call from throwing on the money path.
 */
export function pickWinningSegmentIndex(weights: readonly number[], draw: number): number {
  if (weights.length === 0) return -1;
  if (draw < 0) return 0;
  let cumulative = 0;
  for (let i = 0; i < weights.length; i += 1) {
    cumulative += Math.max(0, weights[i] ?? 0);
    if (draw < cumulative) return i;
  }
  // draw >= totalWeight (shouldn't happen for a valid draw) → last segment.
  return weights.length - 1;
}

/** Sum of the segment weights (the RNG's inclusive-exclusive upper bound). */
export function totalSegmentWeight(weights: readonly number[]): number {
  return weights.reduce((sum, w) => sum + Math.max(0, w ?? 0), 0);
}

/**
 * Approximate win percentage per segment (weight / total), rounded to one
 * decimal for the operator authoring UI. Returns all-zero when the total weight
 * is 0 (no segments / all-zero weights) so the caller never divides by zero.
 */
export function segmentWinPercents(weights: readonly number[]): number[] {
  const total = totalSegmentWeight(weights);
  if (total <= 0) return weights.map(() => 0);
  return weights.map((w) => Math.round((Math.max(0, w ?? 0) / total) * 1000) / 10);
}

/**
 * Whether the wheel is actually PLAYABLE for a tenant: the operator switched the
 * feature on AND authored at least `SPIN_WHEEL_MIN_SEGMENTS` segments. Both the
 * spin callable (`unavailable` guard) and the customer surface (empty state) use
 * this so a half-configured wheel (flag on, no segments yet) never dead-ends —
 * the customer simply doesn't see a broken wheel.
 */
export function isSpinWheelPlayable(config: {
  readonly enabledFeatures: { readonly spinWheel?: boolean };
  readonly spinWheel?: { readonly segments?: readonly unknown[] };
}): boolean {
  return (
    config.enabledFeatures.spinWheel === true &&
    (config.spinWheel?.segments?.length ?? 0) >= SPIN_WHEEL_MIN_SEGMENTS
  );
}

export interface SpinGrantIds {
  /** Deterministic points-ledger entry id for a spin win. */
  readonly ledgerId: string;
  /** Deterministic issued-voucher id for a spin win. */
  readonly voucherId: string;
}

/**
 * Deterministic grant ids for one spin, keyed by a freshly-minted `spinId`. Both
 * stay inside ShortId's 24-char cap when `spinId` is <= 19 chars (`spin_` = 5,
 * `sp_` = 3), which the spin callable's generator guarantees. The determinism
 * mirrors the punch-card/challenge/referral grants: an in-transaction existence
 * check on these ids makes a retried write a true no-op (belt-and-suspenders —
 * a fresh `spinId` is unique per call, so there is nothing to collide with).
 */
export function spinGrantIds(spinId: string): SpinGrantIds {
  return { ledgerId: `spin_${spinId}`, voucherId: `sp_${spinId}` };
}
